// src/pages/reports/SalaryReports.jsx
import { useState, useEffect, useMemo } from 'react'
import { ChevronDown, X } from 'lucide-react'
import BaseReportPage from '../../components/reports/BaseReportPage'
import BranchService from '../../services/branchService'
import employeeService from '../../services/employeeService'
import jobService from '../../services/jobService'
import AuthService from '../../services/authService'

const SalaryFilters = ({ filters, onFiltersChange, onApply, onClear }) => {
  const [branches, setBranches] = useState([])
  const [jobs, setJobs] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Get user role and branch
  const user = AuthService.getCurrentUser()
  const isManager = user?.role === 'manager'
  const userBranchId = user?.selectedBranch?._id || user?.branchId || user?.branch?._id
  
  // Initialize manager's branch filter on mount
  useEffect(() => {
    if (isManager && userBranchId && onFiltersChange) {
      const initialFilters = {
        ...filters,
        branch: userBranchId
      }
      console.log('🔧 Manager detected - Setting initial branch filter:', userBranchId)
      onFiltersChange(initialFilters)
    }
  }, [isManager, userBranchId]) // eslint-disable-line react-hooks/exhaustive-deps
  
  // Load branches, jobs, and employees from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        console.log('🔄 SalaryFilters: Loading filter data...')
        
        // Load all data in parallel
        const [branchResponse, jobResponse, employeeResponse] = await Promise.allSettled([
          BranchService.getAllBranches(),
          jobService.getAllJobs(),
          employeeService.getAllEmployees()
        ])
        
        console.log('📊 SalaryFilters API responses:', { branchResponse, jobResponse, employeeResponse })
        
        // Process branches
        let branchesData = []
        if (branchResponse.status === 'fulfilled' && branchResponse.value?.success) {
          const response = branchResponse.value
          console.log('🏢 SalaryFilters: Branch response data:', response.data)
          if (response.data && response.data.branches && Array.isArray(response.data.branches)) {
            branchesData = response.data.branches
          } else if (Array.isArray(response.data)) {
            branchesData = response.data
          }
        } else {
          console.error('❌ SalaryFilters: Branch API failed:', branchResponse)
        }
        console.log('🏢 SalaryFilters: Final branches data:', branchesData)
        setBranches(branchesData)
        
        // Process jobs
        let jobsData = []
        if (jobResponse.status === 'fulfilled' && jobResponse.value?.success) {
          const response = jobResponse.value
          console.log('💼 SalaryFilters: Jobs response data:', response.data)
          if (response.data && response.data.jobs && Array.isArray(response.data.jobs)) {
            jobsData = response.data.jobs
          } else if (Array.isArray(response.data)) {
            jobsData = response.data
          }
        } else {
          console.error('❌ SalaryFilters: Jobs API failed:', jobResponse)
        }
        console.log('💼 SalaryFilters: Final jobs data:', jobsData)
        setJobs(jobsData)
        
        // Process employees
        let employeesData = []
        if (employeeResponse.status === 'fulfilled' && employeeResponse.value?.success) {
          const response = employeeResponse.value
          console.log('👥 SalaryFilters: Employee response data:', response.data)
          if (response.data && response.data.employees && Array.isArray(response.data.employees)) {
            employeesData = response.data.employees
          } else if (Array.isArray(response.data)) {
            employeesData = response.data
          }
        } else {
          console.error('❌ SalaryFilters: Employee API failed:', employeeResponse)
        }
        console.log('👥 SalaryFilters: Final employees data:', employeesData)
        setEmployees(employeesData)
        
      } catch (error) {
        console.error('❌ SalaryFilters: Error loading data:', error)
        // Set empty arrays if API fails
        setBranches([])
        setJobs([])
        setEmployees([])
      } finally {
        console.log('✅ SalaryFilters: Data loading complete')
        setLoading(false)
      }
    }
    
    loadData()
  }, [])

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  const handleClearFilters = () => {
    console.log('🗑️ SalaryFilters: Clear All clicked')
    onFiltersChange({
      branch: isManager ? userBranchId : '',
      job: '',
      employee: '',
      startDate: '',
      endDate: ''
    })
    onClear()
  }

  const hasActiveFilters = useMemo(() => {
    const active = (!isManager && filters?.branch) || filters?.job || filters?.employee || filters?.startDate || filters?.endDate
    console.log('🔍 hasActiveFilters check:', { filters, active })
    return active
  }, [filters, isManager])

  // Dynamic job options from API
  const jobOptions = useMemo(() => {
    console.log('🔄 SalaryFilters: Creating job options from jobs:', jobs)
    const options = [{ value: '', label: 'All Jobs' }]
    
    jobs.forEach(job => {
      console.log('💼 Processing job:', job)
      if (job._id && (job.title || job.name)) {
        const option = {
          value: job._id,
          label: job.title || job.name
        }
        options.push(option)
        console.log('✅ Added job option:', option)
      } else {
        console.log('❌ Skipped job (missing _id or title/name):', job)
      }
    })
    
    console.log('📋 Final job options:', options)
    return options
  }, [jobs])

  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >

            <span>Clear Filters</span>
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Branch Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Branch
          </label>
          <div className="relative">
            {isManager ? (
              // Manager: Read-only branch display
              <div className="w-full bg-gray-50 border border-gray-300 rounded-md py-2 pl-3 pr-3 text-sm text-gray-700 font-medium">
                {branches.find(b => b._id === userBranchId)?.name || 'My Branch'}
              </div>
            ) : (
              // Admin: Branch dropdown
              <>
                <select
                  value={filters?.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={loading}
                >
                  <option value="">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
              </>
            )}
          </div>
        </div>

        {/* Jobs Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Jobs
          </label>
          <div className="relative">
            <select
              value={filters?.job || ''}
              onChange={(e) => handleFilterChange('job', e.target.value)}
              className="w-full appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              {jobOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Employee Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Employee
          </label>
          <div className="relative">
            <select
              value={filters?.employee || ''}
              onChange={(e) => handleFilterChange('employee', e.target.value)}
              className="w-full appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              disabled={loading}
            >
              <option value="">All Employees</option>
              {employees.map((employee) => (
                <option key={employee._id} value={employee._id}>
                  {employee.fullName || employee.name} ({employee.employeeId})
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Start Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            value={filters?.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="w-full bg-white border border-gray-300 rounded-md py-2 pl-3 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>

        {/* End Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            value={filters?.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="w-full bg-white border border-gray-300 rounded-md py-2 pl-3 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
      </div>
    </div>
  )
}

const SalaryReports = () => {

  // Define table columns as per requirements
  const columns = [
    { key: 'employeeName', label: 'Employee Name', sortable: true, type: 'text' },
    { key: 'designation', label: 'Designation', sortable: true, type: 'text' },
    { key: 'salaryMonth', label: 'Salary Month', sortable: true, type: 'text' },
    { key: 'basicSalary', label: 'Basic Salary', sortable: true, type: 'currency' },
    { key: 'deduction', label: 'Deduction', sortable: true, type: 'currency' },
    { key: 'allowances', label: 'Allowances', sortable: true, type: 'currency' },
    { key: 'netSalary', label: 'Net Salary', sortable: true, type: 'currency' },
    { key: 'branch', label: 'Branch', sortable: true, type: 'text' }
  ]

  return (
    <BaseReportPage
      title="Salary Reports"
      description="Employee salary information with comprehensive filtering options"
      reportType="salaryReports"
      columns={columns}
      customFilterComponent={SalaryFilters}
      showExport={true}
      showPrint={true}
    />
  )
}

export default SalaryReports